import React from 'react';
import { SkeletonGroup, SkeletonText } from '@shared/components/Skeleton';
import { useTranslation } from '@shared/core';
import { ProductGrid } from '../../../../components';
import { useRecommendedProductsSectionController } from './RecommendedProductsSection.controller';
import { StyledContainer, StyledTitle, styles } from './RecommendedProductsSection.styles';
import { withLazyPropOnIntersection } from '@shared/utils/hooks/useLazyLoadImage';
import { useShopStorefrontContext } from '../../../../components/ShopStorefrontProvider';

interface RecommendedProductsSectionProps {
  eventId: Maybe<string>;
  showSkeleton?: boolean;
  hideHeader?: boolean;
  disableDeferredLoading?: boolean;
}

export const Root = React.forwardRef<HTMLDivElement, RecommendedProductsSectionProps & { isOnScreen: boolean }>((props, ref) => {
  const { eventId, showSkeleton, isOnScreen, hideHeader } = props;

  const { t } = useTranslation('catalogRegistry');
  const translations = t('recommendedProductsSection');
  const { currentStorefront } = useShopStorefrontContext();
  const sectionTitle = translations[currentStorefront]?.sectionTitle?.();
  const { products, isMobile, hasLoadingError, loading, addCatalogItemToRegistry, addCatalogItemLoading, telemetryPageValue, onLoadMore, hasNextPage, isFetchingMore } =
    useRecommendedProductsSectionController({ eventId, isOnScreen });

  const showProductGridSkeleton = showSkeleton || loading || hasLoadingError || !isOnScreen;

  return (
    <StyledContainer
      __css={styles.container}
      ref={ref}
    >
      <SkeletonGroup
        isReady={!showSkeleton}
        placeholder={
          <SkeletonText
            rows={2}
            skeletonHeight={56}
            width={500}
            marginBottom={10}
            marginTop={10}
            alignSelf="center"
          />
        }
        display={'flex'}
        justifyContent={'center'}
      >
        <StyledTitle
          {...styles.title}
          typographyVariant={['HedMobile32', null, 'Hed56']}
        >
          <span>{sectionTitle}</span>
        </StyledTitle>
      </SkeletonGroup>
      <ProductGrid
        maxColumns={4}
        maxWidth={1680}
        isMobile={isMobile}
        filters={[]}
        searchLoading={loading}
        showSkeleton={showProductGridSkeleton}
        categoryLocator={null}
        componentSource="productRec"
        categoryType="category"
        telemetryPageValue={telemetryPageValue}
        products={products}
        addCatalogItem={addCatalogItemToRegistry}
        addItemLoading={addCatalogItemLoading}
        isFetchingMore={isFetchingMore}
        onLoadMoreClick={onLoadMore}
        hasNextPage={hasNextPage}
        disableGridMarginBottom={false}
        disableFilters={true}
        hideFilterToggle={true}
        hideHeader={hideHeader}
        shouldOpenPdp={true}
      />
    </StyledContainer>
  );
});

const LazyLoadingRecommendedProductsSection = withLazyPropOnIntersection(Root, 'isOnScreen');

const DEFERRED_LOADING_PROPS = { onscreen: true, offscreen: false };
const NON_DEFERRED_LOADING_PROPS = { onscreen: true, offscreen: true };

export const RecommendedProductsSection: React.FC<RecommendedProductsSectionProps> = props => {
  const { disableDeferredLoading } = props;
  return (
    <LazyLoadingRecommendedProductsSection
      isOnScreen={disableDeferredLoading ? NON_DEFERRED_LOADING_PROPS : DEFERRED_LOADING_PROPS}
      {...props}
    />
  );
};
